import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { ServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/utils/types';
import { useDeleteServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/api/useDeleteServicePlanBillingPolicy';
import { LIST_SERVICE_PLAN_BILLING_POLICIES_RESOURCE } from 'src/features/servicePlanBillingPolicies/api/useListServicePlanBillingPolicies';
import { billingPoliciesMessages } from 'src/features/servicePlanBillingPolicies/utils/messages';
import { useParams } from 'react-router-dom';
import { useOnError } from 'src/utils/errors';

interface DeleteServicePlanBillingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanBillingPolicy: ServicePlanBillingPolicy;
    children?: React.ReactNode;
}

function DeleteServicePlanBillingPolicyFormModal<TData>({
    selectedServicePlanBillingPolicy,
    children,
}: DeleteServicePlanBillingPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const onError = useOnError();

    const { mutate: deleteBillingPolicy } = useDeleteServicePlanBillingPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_BILLING_POLICIES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(billingPoliciesMessages.deleteServicePlanBillingPolicySuccess),
                description: formatMessage(billingPoliciesMessages.deleteServicePlanBillingPolicySuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...billingPoliciesMessages.deleteServicePlanBillingPolicy },
                subTitle: {
                    ...billingPoliciesMessages.deleteServicePlanBillingPolicySubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...billingPoliciesMessages.confirmDeleteServicePlanBillingPolicy },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: billingPoliciesMessages.deleteServicePlanBillingPolicy,
                callbackFn: () => {
                    return (
                        deleteBillingPolicy({
                            servicePlanItemId,
                            billingPolicyId: selectedServicePlanBillingPolicy.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanBillingPolicyFormModal;
